import { useContext, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
    fetchSkillMatrixDefaultFilters,
    fetchSkillMatrixFilters,
} from "../../../../js/features/ManageWorkers/ManageWorkersSlice";
import {
    FILTER_JOB_KEY,
    FILTER_LOCATION_KEY,
    FILTER_MANAGER_KEY,
    FILTER_PRIMARY_JOB_KEY,
    FILTER_SHIFT_PATTERN_KEY,
    FILTER_SKILL_KEY,
    FILTER_SKILL_PRIORITIES_KEY,
    FILTER_WORK_CENTERS_KEY,
} from "../../../assets/constants";
import { FILTER_WORKER_TYPE_KEY } from "../../../assets/constants";
import {
    JOB_ROLES,
    LOCATIONS,
    PRIMARY_JOB_ROLES,
    REPORTING_TO,
    SHIFT_PATTERNS,
    SKILL_PRIORITIES,
    WORK_CENTERS,
} from "../../filters/constants";
import { SKILLS, WORKER_TYPES } from "../../filters/constants";
import { AppliedFilter, DataRelation, FilterContext } from "../../filters/filter-context";

export const useSkillMatrixFilters = () => {
    const dispatch = useDispatch();

    const allLocationsData = useSelector((state: any) => state.manageWorkers?.skillMatrixFiltersLocations ?? []);
    const allWorkCentersData = useSelector((state: any) => state.manageWorkers?.skillMatrixFiltersWorkCenters ?? []);
    const allManagersData = useSelector((state: any) => state.manageWorkers?.skillMatrixFiltersManagers ?? []);
    const allJobRolesData = useSelector((state: any) => state.manageWorkers?.skillMatrixFiltersAllJobs ?? []);
    const workerTypesData = useSelector((state: any) => state.manageWorkers?.skillMatrixFiltersWorkerTypes ?? []);
    const shiftPatternsData = useSelector((state: any) => state.manageWorkers?.skillMatrixFiltersShiftPatterns ?? []);
    const workerSubTypesData = useSelector((state: any) => state.manageWorkers?.skillMatrixFiltersWorkerSubTypes ?? []);
    const allSkillsData = useSelector((state: any) => state.manageWorkers?.skillMatrixFiltersSkills ?? []);
    const skillLevelsData = useSelector((state: any) => state.manageWorkers?.skillMatrixFiltersSkillLevels ?? []);
    const skillPrioritiesData = useSelector((state: any) => state.manageWorkers?.skillMatrixFiltersSkillPriorities ?? []);
    const filterRelationshipsData = useSelector((state: any) => state.manageWorkers?.skillMatrixFiltersFilterRelationships ?? []);
    const skillMatrixFiltersStatus = useSelector((state: any) => state.manageWorkers?.fetchSkillMatrixFilters?.status || "idle");
    const isWorkerSubTypeEnabled = useSelector((state: any) => state.user?.userData?.workerSubTypeEnabled || false);
    const defaultFiltersData = useSelector((state: any) => state.manageWorkers?.skillMatrixAppliedFilters ?? null);

    const filterContext = useContext(FilterContext);
    if (!filterContext) {
        throw new Error("useSkillMatrixFilters must be used within a FilterProvider");
    }
    const { 
        appliedFilters, 
        setAppliedFilters, 
        open: isFilterOpen, 
        setFilterOpen, 
        setFilters, 
        filters, 
        setDataRelations, 
        filterAccordionHeight, 
        setIsLoading 
    } = filterContext;

    // Fetch filters and default filters on mount
    useEffect(() => {
        setIsLoading?.(true);
        dispatch(fetchSkillMatrixFilters());
        dispatch(fetchSkillMatrixDefaultFilters());
    }, [dispatch, setIsLoading]);

    // Setup filters from API response
    useEffect(() => {
        if (skillMatrixFiltersStatus === "fulfilled") {
            setIsLoading?.(false);
            try {
                const filters: any[] = [];

                // Locations
                if (allLocationsData?.length > 0) {
                    filters.push({
                        id: 1,
                        name: LOCATIONS,
                        key: FILTER_LOCATION_KEY,
                        data: allLocationsData,
                        reducedData: allLocationsData,
                        searchedData: allLocationsData,
                    });
                }

                // Work Centers
                if (allWorkCentersData?.length > 0) {
                    filters.push({
                        id: 2,
                        name: WORK_CENTERS,
                        key: FILTER_WORK_CENTERS_KEY,
                        data: allWorkCentersData,
                        reducedData: allWorkCentersData,
                        searchedData: allWorkCentersData,
                    });
                }

                // Primary Job Roles
                if (allJobRolesData?.length > 0) {
                    filters.push({
                        id: 3,
                        name: PRIMARY_JOB_ROLES,
                        key: FILTER_PRIMARY_JOB_KEY,
                        data: allJobRolesData,
                        reducedData: allJobRolesData,
                        searchedData: allJobRolesData,
                    });
                }

                // Job Roles
                if (allJobRolesData?.length > 0) {
                    filters.push({
                        id: 4,
                        name: JOB_ROLES,
                        key: FILTER_JOB_KEY,
                        data: allJobRolesData,
                        reducedData: allJobRolesData,
                        searchedData: allJobRolesData,
                    });
                }

                // Worker Types (with subtypes if enabled, flattened for hierarchical filter list)
                if (workerTypesData?.length > 0) {
                    const workerTypeData: Array<{
                        id: number;
                        name: string;
                        parentId?: number;
                    }> = [];

                    // Add main worker types
                    workerTypesData.forEach((type: any) => {
                        workerTypeData.push({
                            id: parseInt(type.id.toString()),
                            name: type.name,
                        });
                    });

                    // Add subtypes with offset if enabled
                    if (workerSubTypesData?.length > 0 && isWorkerSubTypeEnabled) {
                        workerSubTypesData.forEach((subType: any) => {
                            workerTypeData.push({
                                id: parseInt(subType.id.toString()) + 1000,
                                name: subType.name,
                                parentId: parseInt(subType.workerTypeId.toString()),
                            });
                        });
                    }

                    filters.push({
                        id: 5,
                        name: WORKER_TYPES,
                        key: FILTER_WORKER_TYPE_KEY,
                        data: workerTypeData,
                        reducedData: workerTypeData,
                        searchedData: workerTypeData,
                    });
                }

                // Reporting To (Managers)
                if (allManagersData?.length > 0) {
                    filters.push({
                        id: 6,
                        name: REPORTING_TO,
                        key: FILTER_MANAGER_KEY,
                        data: allManagersData,
                        reducedData: allManagersData,
                        searchedData: allManagersData,
                    });
                }

                // Shift Patterns
                if (shiftPatternsData?.length > 0) {
                    filters.push({
                        id: 7,
                        name: SHIFT_PATTERNS,
                        key: FILTER_SHIFT_PATTERN_KEY,
                        data: shiftPatternsData,
                        reducedData: shiftPatternsData,
                        searchedData: shiftPatternsData,
                    });
                }

                // Skills (with skill levels as children, flattened for hierarchical filter list)
                if (allSkillsData?.length > 0) {
                    const flatSkillData: any[] = [];
                    allSkillsData.forEach((skill: any) => {
                        const skillId = parseInt(skill.id.toString());
                        // Add parent skill (explicitly remove parentId to ensure it's treated as root)
                        const { parentId: _removed, ...skillWithoutParentId } = skill;
                        flatSkillData.push({ ...skillWithoutParentId, id: skillId });
                        // Add child skill levels with composite IDs
                        skillLevelsData.forEach((level: any) => {
                            const levelId = parseInt(level.id.toString());
                            flatSkillData.push({
                                ...level,
                                id: skillId * 100000 + levelId,
                                name: level.name,
                                parentId: skillId,
                            });
                        });
                    });

                    filters.push({
                        id: 8,
                        name: SKILLS,
                        key: FILTER_SKILL_KEY,
                        data: flatSkillData,
                        reducedData: flatSkillData,
                        searchedData: flatSkillData,
                    });
                }

                // Skill Priorities
                if (skillPrioritiesData?.length > 0) {
                    filters.push({
                        id: 9,
                        name: SKILL_PRIORITIES,
                        key: FILTER_SKILL_PRIORITIES_KEY,
                        data: skillPrioritiesData,
                        reducedData: skillPrioritiesData,
                        searchedData: skillPrioritiesData,
                    });
                }

                setFilters(filters);

                // Set data relations for cascading filters
                if (filterRelationshipsData?.length > 0) {
                    let relations: DataRelation[] = filterRelationshipsData.map((rel: any) => ({
                        from: rel.from,
                        to: rel.to,
                        mappings: rel.mappings,
                    }));

                    // Clone work center -> job relation for primary jobs
                    const workCenterToJob = relations.find(
                        r => r.from === FILTER_WORK_CENTERS_KEY && r.to === FILTER_JOB_KEY
                    );

                    if (workCenterToJob) {
                        const primaryJobRelation: DataRelation = {
                            from: FILTER_WORK_CENTERS_KEY,
                            to: FILTER_PRIMARY_JOB_KEY,
                            mappings: workCenterToJob.mappings,
                        };
                        relations = [...relations, primaryJobRelation];
                    }

                    setDataRelations(relations);
                }
            } catch (error) {
                console.error("Error setting up skill matrix filters:", error);
                setIsLoading?.(false);
            }
        }
    }, [skillMatrixFiltersStatus, allLocationsData, allWorkCentersData, allManagersData, allJobRolesData, workerTypesData, shiftPatternsData, workerSubTypesData, allSkillsData, skillLevelsData, skillPrioritiesData, filterRelationshipsData, setFilters, setDataRelations, isWorkerSubTypeEnabled, setIsLoading]);

    // Build defaultAppliedFilters from the default filters API response + raw data arrays (stable, like Job Matrix)
    const hasAppliedDefaults = useRef(false);
    const defaultAppliedFilters = useMemo((): AppliedFilter[] => {
        if (!defaultFiltersData || skillMatrixFiltersStatus !== "fulfilled") return [];
        const result: AppliedFilter[] = [];
        const now = Date.now();
        const parseIds = (val: any): number[] => {
            if (!val) return [];
            if (typeof val === "number") return [val];
            if (typeof val === "string") return val.split(",").map(id => parseInt(id.trim())).filter(id => !isNaN(id));
            if (Array.isArray(val)) return val.map(id => typeof id === "string" ? parseInt(id) : id);
            return [];
        };


        // Location
        const locationIds = parseIds(defaultFiltersData.locations);
        const locationData = allLocationsData?.filter((l: any) => locationIds.includes(l.id));
        if (locationData?.length > 0) {
            result.push({ type: LOCATIONS, key: FILTER_LOCATION_KEY, data: locationData, timeAdded: now });
        }

        // Work Center
        const wcIds = parseIds(defaultFiltersData.workcenters);
        const wcData = allWorkCentersData?.filter((w: any) => wcIds.includes(w.id));
        if (wcData?.length > 0) {
            result.push({ type: WORK_CENTERS, key: FILTER_WORK_CENTERS_KEY, data: wcData, timeAdded: now + 1 });
        }

        // Manager
        const managerIds = parseIds(defaultFiltersData.managers);
        const managerData = allManagersData?.filter((m: any) => managerIds.includes(m.id));
        if (managerData?.length > 0) {
            result.push({ type: REPORTING_TO, key: FILTER_MANAGER_KEY, data: managerData, timeAdded: now + 2 });
        }

        // Shift Pattern
        const shiftPatternIds = parseIds(defaultFiltersData.shiftPatterns);
        const shiftData = shiftPatternsData?.filter((s: any) => shiftPatternIds.includes(s.id));
        if (shiftData?.length > 0) {
            result.push({ type: SHIFT_PATTERNS, key: FILTER_SHIFT_PATTERN_KEY, data: shiftData, timeAdded: now + 3 });
        }

        // Worker Type
        const workerTypeIds = parseIds(defaultFiltersData.workerTypes);
        const workerTypeData = workerTypesData?.filter((w: any) => workerTypeIds.includes(w.id));
        if (workerTypeData?.length > 0) {
            result.push({ type: WORKER_TYPES, key: FILTER_WORKER_TYPE_KEY, data: workerTypeData, timeAdded: now + 4 });
        }

        // Job Role — compare against reduced jobs (after work center cascading) if work centers were filtered
        const jobIds = parseIds(defaultFiltersData.jobs);
        const jobData = allJobRolesData?.filter((j: any) => jobIds.includes(j.id));
        let availableJobCount = allJobRolesData?.length ?? 0;
        if (wcData?.length > 0) {
            // Simulate cascading: find jobs available under the selected work centers
            const wcToJobRelation = filterRelationshipsData?.find(
                (r: any) => r.from === FILTER_WORK_CENTERS_KEY && r.to === FILTER_JOB_KEY
            );
            if (wcToJobRelation?.mappings) {
                const selectedWcIds = wcData.map((w: any) => w.id);
                const allowedJobIds = wcToJobRelation.mappings
                    .filter((m: any) => selectedWcIds.includes(m.fromId))
                    .flatMap((m: any) => Array.isArray(m.toIds) ? m.toIds : []);
                const reducedJobs = allJobRolesData?.filter((j: any) => allowedJobIds.includes(j.id));
                if (reducedJobs?.length > 0) {
                    availableJobCount = reducedJobs.length;
                }
            }
        }
        if (jobData?.length > 0) {
            result.push({ type: JOB_ROLES, key: FILTER_JOB_KEY, data: jobData, timeAdded: now + 5 });
        }

        // Primary Job (uses same defaultFiltersData.jobs as Job Role)
        if (jobData?.length > 0) {
            result.push({ type: PRIMARY_JOB_ROLES, key: FILTER_PRIMARY_JOB_KEY, data: jobData, timeAdded: now + 6 });
        }

        // Skill
        const skillIds = parseIds(defaultFiltersData.skills);
        const skillData = allSkillsData?.filter((sk: any) => skillIds.includes(sk.id));
        if (skillData?.length > 0) {
            result.push({ type: SKILLS, key: FILTER_SKILL_KEY, data: skillData, timeAdded: now + 7 });
        }

        // Skill Priorities
        const skillPriorityIds = parseIds(defaultFiltersData.skillPriorities);
        const priorityData = skillPrioritiesData?.filter((sp: any) => skillPriorityIds.includes(sp.id));
        if (priorityData?.length > 0) {
            result.push({ type: SKILL_PRIORITIES, key: FILTER_SKILL_PRIORITIES_KEY, data: priorityData, timeAdded: now + 8 });
        }

        return result;
    }, [defaultFiltersData, skillMatrixFiltersStatus, allLocationsData, allWorkCentersData, allManagersData, shiftPatternsData, workerTypesData, allJobRolesData, allSkillsData, skillPrioritiesData, filterRelationshipsData]);

    // Apply default filters once when computed, if no filters are currently active
    useEffect(() => {
        if (!hasAppliedDefaults.current && defaultAppliedFilters.length > 0) {
            if (appliedFilters.length === 0) {
                setAppliedFilters(defaultAppliedFilters);
            }
            hasAppliedDefaults.current = true;
        }
    }, [defaultAppliedFilters, appliedFilters.length, setAppliedFilters]);

    return {
        filters,
        setFilters,
        appliedFilters,
        isFilterOpen,
        setFilterOpen,
        filterStatus: skillMatrixFiltersStatus,
        filterAccordionHeight,
    };
};
